// Open-Meteo: API gratuita, sin API key. https://open-meteo.com — a
// diferencia del resto de los repositorios HTTP, este le habla directo a un
// servicio externo, no a nuestro backend (por eso no usa apiRequest/client.ts).
import type { WeatherRepository } from '../../application/ports/WeatherRepository';
import type { AgroForecast, GeocodeResult, WeatherData, WeatherPoint } from '../../domain/Weather';

export class HttpWeatherRepository implements WeatherRepository {
  async searchCities(query: string): Promise<GeocodeResult[]> {
    if (!query.trim()) return [];
    const res = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=es&format=json`
    );
    if (!res.ok) throw new Error('No se pudo buscar la ciudad.');
    const data = await res.json();
    return data.results ?? [];
  }

  // BigDataCloud: reverse geocoding gratuito pensado para uso directo desde el navegador (sin API key).
  async reverseGeocode(lat: number, lon: number): Promise<string | null> {
    try {
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=es`
      );
      if (!res.ok) return null;
      const data = await res.json();
      const place = data.city || data.locality || data.principalSubdivision;
      if (!place) return null;
      return data.countryName ? `${place}, ${data.countryName}` : place;
    } catch {
      return null;
    }
  }

  async getForecast(lat: number, lon: number): Promise<WeatherData> {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,precipitation` +
        `&hourly=temperature_2m,weather_code,precipitation_probability,wind_speed_10m` +
        `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
        `&timezone=auto&forecast_days=2`
    );
    if (!res.ok) throw new Error('No se pudo obtener el clima.');
    const data = await res.json();

    const times: string[] = data.hourly?.time ?? [];
    const nowIdx = Math.max(
      times.findIndex((t) => t === data.current?.time),
      0
    );
    const hourly: WeatherPoint[] = times.slice(nowIdx, nowIdx + 24).map((time: string, i: number) => ({
      time,
      temperature: data.hourly.temperature_2m[nowIdx + i],
      weatherCode: data.hourly.weather_code[nowIdx + i],
      precipitationProbability: data.hourly.precipitation_probability[nowIdx + i],
      windSpeed: data.hourly.wind_speed_10m[nowIdx + i],
    }));

    return {
      current: {
        temperature: data.current.temperature_2m,
        apparentTemperature: data.current.apparent_temperature,
        weatherCode: data.current.weather_code,
        windSpeed: data.current.wind_speed_10m,
        precipitation: data.current.precipitation,
      },
      daily: {
        temperatureMin: data.daily.temperature_2m_min[0],
        temperatureMax: data.daily.temperature_2m_max[0],
        precipitationProbability: data.daily.precipitation_probability_max?.[0] ?? 0,
      },
      hourly,
    };
  }

  async getAgroForecast(lat: number, lon: number): Promise<AgroForecast> {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&hourly=temperature_2m,relative_humidity_2m,precipitation,precipitation_probability,wind_speed_10m,wind_gusts_10m,wind_direction_10m,weather_code` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,et0_fao_evapotranspiration` +
        `&current=temperature_2m&timezone=auto&forecast_days=7`
    );
    if (!res.ok) throw new Error('No se pudo obtener el pronóstico.');
    const data = await res.json();

    // Las horas arrancan a la medianoche de hoy — se recorta desde la hora
    // actual para que el pronóstico horario empiece en "ahora".
    const times: string[] = data.hourly?.time ?? [];
    const currentHour = typeof data.current?.time === 'string' ? data.current.time.slice(0, 13) : '';
    const nowIdx = Math.max(
      times.findIndex((t) => t.slice(0, 13) === currentHour),
      0
    );
    const h = data.hourly;
    const hourly = times.slice(nowIdx, nowIdx + 48).map((time, i) => {
      const idx = nowIdx + i;
      return {
        time,
        temperature: h.temperature_2m[idx],
        humidity: h.relative_humidity_2m[idx],
        precipitation: h.precipitation[idx] ?? 0,
        precipitationProbability: h.precipitation_probability[idx] ?? 0,
        windSpeed: h.wind_speed_10m[idx],
        windGusts: h.wind_gusts_10m[idx],
        windDirection: h.wind_direction_10m[idx],
        weatherCode: h.weather_code[idx],
      };
    });

    const d = data.daily;
    const daily = (d?.time ?? []).map((date: string, i: number) => ({
      date,
      weatherCode: d.weather_code[i],
      temperatureMin: d.temperature_2m_min[i],
      temperatureMax: d.temperature_2m_max[i],
      precipitationSum: d.precipitation_sum[i] ?? 0,
      precipitationProbability: d.precipitation_probability_max[i] ?? 0,
      windSpeedMax: d.wind_speed_10m_max[i],
      windGustsMax: d.wind_gusts_10m_max[i],
      windDirectionDominant: d.wind_direction_10m_dominant[i],
      et0: d.et0_fao_evapotranspiration[i] ?? 0,
    }));

    return { hourly, daily };
  }
}
